/* ══════════════════════════════════════════════════════════════
   Border Glow — portage Shopify/vanilla du composant React Bits.

   Reprend telle quelle la logique du composant d'origine :
   - getEdgeProximity : 0 au centre de la carte, 100 sur le bord
   - getCursorAngle   : angle curseur→centre, 0° en haut, sens horaire
   Les deux valeurs sont écrites dans --edge-proximity / --cursor-angle,
   et c'est le CSS qui décide de l'opacité de chaque couche.

   Sélecteurs ciblés : window.LOCKIN_BORDER_GLOW_SELECTORS
   API : window.LockinBorderGlow.refresh()
══════════════════════════════════════════════════════════════ */
(() => {
  if (window.LockinBorderGlow) return;

  const DEFAULT_SELECTORS = [
    '.aura-stat',
    '.aura-review',
    '.aura-sim__card',
    '.aura-parent__card',
    '.lk-which__card',
    '.lk-painpoint-card',
    '.lk-feature-card',
    '.lk-guarantee__card',
    '.lk-testimonial-card',
    '.lk-step',
  ];

  const supportsMask =
    typeof CSS !== 'undefined' &&
    typeof CSS.supports === 'function' &&
    (CSS.supports('mask-composite', 'exclude') || CSS.supports('-webkit-mask-composite', 'xor'));

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  const equipped = new WeakSet();

  const selectors = () =>
    (Array.isArray(window.LOCKIN_BORDER_GLOW_SELECTORS) && window.LOCKIN_BORDER_GLOW_SELECTORS.length
      ? window.LOCKIN_BORDER_GLOW_SELECTORS
      : DEFAULT_SELECTORS
    ).join(',');

  const enabled = () => supportsMask && finePointer.matches && !prefersReducedMotion.matches;

  /** 0 au centre, 1 sur le bord (identique au composant d'origine). */
  function getEdgeProximity(width, height, x, y) {
    const cx = width / 2;
    const cy = height / 2;
    const dx = x - cx;
    const dy = y - cy;
    let kx = Infinity;
    let ky = Infinity;
    if (dx !== 0) kx = cx / Math.abs(dx);
    if (dy !== 0) ky = cy / Math.abs(dy);
    return Math.min(Math.max(1 / Math.min(kx, ky), 0), 1);
  }

  /** 0° = haut, sens horaire — convention de conic-gradient. */
  function getCursorAngle(width, height, x, y) {
    const dx = x - width / 2;
    const dy = y - height / 2;
    if (dx === 0 && dy === 0) return 0;
    let degrees = Math.atan2(dy, dx) * (180 / Math.PI) + 90;
    if (degrees < 0) degrees += 360;
    return degrees;
  }

  function onPointerMove(event) {
    const card = event.currentTarget;
    const rect = card.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const edge = getEdgeProximity(rect.width, rect.height, x, y);
    const angle = getCursorAngle(rect.width, rect.height, x, y);

    card.style.setProperty('--edge-proximity', (edge * 100).toFixed(3));
    card.style.setProperty('--cursor-angle', `${angle.toFixed(3)}deg`);
  }

  function equip(card) {
    if (equipped.has(card)) return;
    equipped.add(card);

    const cone = document.createElement('span');
    cone.className = 'bglow-cone';
    cone.setAttribute('aria-hidden', 'true');
    cone.innerHTML = '<span class="bglow-ring"></span>';

    const fill = document.createElement('span');
    fill.className = 'bglow-fill';
    fill.setAttribute('aria-hidden', 'true');

    const edgeLight = document.createElement('span');
    edgeLight.className = 'edge-light';
    edgeLight.setAttribute('aria-hidden', 'true');

    card.classList.add('border-glow-card');
    card.append(cone, fill, edgeLight);
    card.addEventListener('pointermove', onPointerMove, { passive: true });
  }

  function refresh() {
    if (!enabled()) return;
    document.querySelectorAll(selectors()).forEach(equip);
  }

  window.LockinBorderGlow = { refresh };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh, { once: true });
  } else {
    refresh();
  }

  // Éditeur de thème Shopify : les sections sont réinjectées à chaud.
  document.addEventListener('shopify:section:load', refresh);
})();
